import {
  getLocalAuthHeader,
  LCCComponentListOUT,
  LCCComponentResult,
  LCCConvention,
  LCCConventionInfo,
  LCCSummaryOUT,
  LCCTotals,
  lccApi,
} from "api-client";
import { makeAutoObservable } from "mobx";
import { buildingStore } from "store/Building/BuildingStore";
import { conformityStore } from "store/ConformityStore";
import i18n from "languages/i18n";

function getConvention(): LCCConvention {
  const certName = conformityStore.certificationOptions.find(
    (c) => c.id === buildingStore.currentBuilding?.certification_id
  )?.name;
  if (certName?.includes("BNB")) return "bnb";
  if (certName?.includes("QNG")) return "qng";
  return "dgnb23";
}

class LCCStore {
  totals: LCCTotals | null = null;
  summary: LCCSummaryOUT | null = null;
  components: LCCComponentResult[] = [];
  componentsTotal = 0;
  componentsPage = 0;
  loadedPages: Set<number> = new Set();
  loadingTotals = false;
  loadingComponents = false;
  sortBy = false;
  error: string | null = null;

  conventions: Record<string, LCCConventionInfo> | null = null;
  conventionDefaults: LCCConventionInfo | null = null;
  conventionOverrides: LCCConventionInfo | null = null;
  selectedConvention: LCCConvention = getConvention();
  loadingConventions = false;

  setSortBy(value: boolean) {
    this.sortBy = value;
    this.setComponents([]);
    this.setComponentsPage(0);
    this.setComponentsTotal(0);
    this.loadedPages = new Set();
    this.fetchComponents(0, 25);
  }

  setTotals(totals: LCCTotals | null) {
    this.totals = totals;
  }

  setSummary(summary: LCCSummaryOUT | null) {
    this.summary = summary;
  }

  setComponents(components: LCCComponentResult[]) {
    this.components = components;
  }

  setComponentsTotal(total: number) {
    this.componentsTotal = total;
  }

  setComponentsPage(page: number) {
    this.componentsPage = page;
  }

  setLoadingTotals(loading: boolean) {
    this.loadingTotals = loading;
  }

  setLoadingComponents(loading: boolean) {
    this.loadingComponents = loading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  setConventions(conventions: Record<string, LCCConventionInfo> | null) {
    this.conventions = conventions;
  }

  setConventionDefaults(defaults: LCCConventionInfo | null) {
    this.conventionDefaults = defaults;
  }

  setConventionOverrides(overrides: LCCConventionInfo | null) {
    this.conventionOverrides = overrides;
  }

  setSelectedConvention(convention: LCCConvention) {
    this.selectedConvention = convention;
    if (this.conventions?.[convention]) {
      this.setConventionDefaults(this.conventions[convention]);
    }
    this.setConventionOverrides(null);
  }

  setLoadingConventions(loading: boolean) {
    this.loadingConventions = loading;
  }

  async fetchConventions() {
    if (this.conventions) return;
    this.setLoadingConventions(true);
    const authHeader = getLocalAuthHeader();
    try {
      const response = await lccApi.reportApiV1RoutersLccListConventions(
        authHeader
      );
      const data = response.data as Record<string, LCCConventionInfo>;
      this.setConventions(data);
      this.setSelectedConvention(this.selectedConvention);
    } catch (err) {
      console.error(err, "LCC fetchConventions.error");
      this.setError(i18n.t("lcc.errorFetchingConventions"));
    } finally {
      this.setLoadingConventions(false);
    }
  }

  applyConventionOverrides(overrides: LCCConventionInfo | null) {
    this.setConventionOverrides(overrides);
    this.setComponents([]);
    this.setComponentsTotal(0);
    this.setComponentsPage(0);
    this.loadedPages = new Set();
    this.fetchTotals();
    this.fetchComponents(0, 25);
  }

  async fetchTotals() {
    const buildingId = buildingStore.buildingID;
    if (!buildingId) return;
    this.setLoadingTotals(true);
    this.setError(null);
    const authHeader = getLocalAuthHeader();
    const convention = this.selectedConvention;
    try {
      const response = await lccApi.reportApiV1RoutersLccLccSummary(
        buildingId,
        {
          convention,
          group_by_kg: false,
          convention_override: this.conventionOverrides ?? undefined,
        },
        authHeader
      );
      const data = response.data as LCCSummaryOUT;
      this.setSummary(data);
      this.setTotals(data.totals);
    } catch (err) {
      console.error(err, "LCC fetchTotals.error");
      this.setError(i18n.t("lcc.errorFetchingTotals"));
    } finally {
      this.setLoadingTotals(false);
    }
  }

  async fetchComponents(page = 0, pageSize = 25) {
    const buildingId = buildingStore.buildingID;
    if (!buildingId) return;
    if (this.loadedPages.has(page)) return;
    this.loadedPages.add(page);
    this.setLoadingComponents(true);
    this.setError(null);
    const authHeader = getLocalAuthHeader();
    const convention = this.selectedConvention;
    try {
      const response = await lccApi.reportApiV1RoutersLccLccComponents(
        buildingId,
        {
          convention,
          page: page + 1,
          page_size: pageSize,
          sort_by: this.sortBy ? "kg" : "name",
          sort_direction: "ASC",
          convention_override: this.conventionOverrides ?? undefined,
        },
        authHeader
      );
      const data = response.data as LCCComponentListOUT;
      if (page === 0) {
        this.setComponents(data.components ?? []);
      } else {
        this.setComponents([...this.components, ...(data.components ?? [])]);
      }
      this.setComponentsTotal(data.components_total ?? 0);
      this.setComponentsPage(page);
    } catch (err) {
      this.loadedPages.delete(page);
      console.error(err, "LCC fetchComponents.error");
      this.setError(i18n.t("lcc.errorFetchingComponents"));
    } finally {
      this.setLoadingComponents(false);
    }
  }

  async loadData() {
    await Promise.all([
      this.fetchConventions(),
      this.fetchTotals(),
      this.fetchComponents(0, 25),
    ]);
  }

  reset() {
    this.totals = null;
    this.summary = null;
    this.components = [];
    this.componentsTotal = 0;
    this.componentsPage = 0;
    this.loadedPages = new Set();
    this.sortBy = false;
    this.loadingTotals = false;
    this.loadingComponents = false;
    this.error = null;
    this.conventions = null;
    this.conventionDefaults = null;
    this.conventionOverrides = null;
    this.selectedConvention = getConvention();
    this.loadingConventions = false;
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: LCCStore;

  static getInstance(): LCCStore {
    if (!LCCStore.instance) {
      LCCStore.instance = new LCCStore();
    }
    return LCCStore.instance;
  }
}

export const lccStore = LCCStore.getInstance();
